'use client';

/**
 * WhatsAppModal Component
 * =======================
 *
 * A modal that appears after form submission to encourage
 * users to also contact via WhatsApp for faster response.
 *
 * FEATURES:
 * - Shows different messages based on email success/failure
 * - Pre-filled WhatsApp message with all form details
 * - Click outside to close
 * - ESC key to close
 * - Animated entrance/exit
 *
 * WHY WHATSAPP?
 * - Faster response time than email
 * - Direct communication channel
 * - Users can get instant answers
 * - Higher conversion rate for real estate leads
 */

import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface WhatsAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  whatsappUrl: string;
  isEmailSuccess: boolean;
  phoneNumber: string;
}

export default function WhatsAppModal({
  isOpen,
  onClose,
  whatsappUrl,
  isEmailSuccess,
  phoneNumber,
}: WhatsAppModalProps) {
  // ============================================================
  // KEYBOARD EVENT HANDLER
  // ============================================================
  // Close modal on ESC key press - good UX practice

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Add/remove keyboard event listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  // Don't render anything if modal is closed
  if (!isOpen) return null;

  // Format phone number for display
  const formattedPhone = `+${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2, 7)} ${phoneNumber.slice(7)}`;

  // ============================================================
  // PORTAL RENDERING
  // ============================================================
  // We use createPortal to render the modal at the document body level.
  // This ensures the modal appears above all other content and isn't
  // affected by parent component styling (like overflow: hidden).

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whatsapp-modal-title"
    >
      {/* Backdrop - click to close */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-white/10 animate-scaleIn">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-white/60 hover:text-white hover:bg-white/10 transition-colors p-3 rounded-full min-w-12 min-h-12 flex items-center justify-center"
          aria-label="Close modal"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-400" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </div>
          <h3 id="whatsapp-modal-title" className="text-2xl font-bold text-white mb-2">
            {isEmailSuccess ? 'Message Sent!' : 'Get Instant Response'}
          </h3>
        </div>

        {/* Content */}
        <div className="text-center mb-6">
          {isEmailSuccess ? (
            <p className="text-white/70">
              Your inquiry has been submitted successfully! For an even <span className="text-green-400 font-semibold">faster response</span>, connect with us directly on WhatsApp.
            </p>
          ) : (
            <p className="text-white/70">
              Having trouble with the form? No worries! Contact us directly on WhatsApp for <span className="text-green-400 font-semibold">immediate assistance</span>.
            </p>
          )}
        </div>

        {/* Benefits */}
        <div className="bg-white/5 rounded-xl p-4 mb-6">
          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-center gap-3 text-white/80">
              <span className="text-green-400">✓</span>
              <span>Instant response within minutes</span>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <span className="text-green-400">✓</span>
              <span>Direct communication with our team</span>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <span className="text-green-400">✓</span>
              <span>Schedule site visits easily</span>
            </div>
          </div>
        </div>

        {/* Phone Number Display */}
        <div className="text-center mb-4">
          <p className="text-white/50 text-sm">WhatsApp Number</p>
          <p className="text-white font-semibold text-lg">{formattedPhone}</p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
            onClick={() => {
              // Close modal after small delay to allow link to open
              setTimeout(onClose, 100);
            }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Open WhatsApp
          </a>

          <button
            onClick={onClose}
            className="w-full py-3 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white font-medium rounded-lg transition-all duration-300"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level
  // This is a React pattern for modals to escape parent overflow:hidden
  if (typeof window === 'undefined') return null;

  return createPortal(modalContent, document.body);
}
